/**
 * SkyModule — IModule for the Hillaire-style physically-based sky.
 *
 * Sets up:
 *   - `SkyRenderer` (inline ray-march sky TSL node)
 *   - `scene.backgroundNode` bound to the sky
 *   - `SkyEnvironmentBaker` that periodically renders the sky into a
 *     PMREM-prefiltered IBL cube and assigns it to `scene.environment`
 *   - Sun-direction tracking (configurable; default reads from provided
 *     callback; otherwise stays at `(0, 1, 0)` zenith)
 *
 * The environment bake is throttled — re-runs only when the sun direction
 * moves by more than `envBakeSunThreshold` radians (default ~1°) or the
 * atmosphere config changes.
 *
 * Usage:
 *   app.use(new SkyModule({
 *     sunDirectionProvider: () => dayNight.getSunDirection(),
 *   }));
 *
 * Declarative (requires HtmlEcsModule):
 *   <x-sky comp="sky.sun=0,1,0; sky.sunIlluminance=20"></x-sky>
 */

import type { App, IModule } from '../../core/App.js';
import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import * as THREE from 'three/webgpu';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import { Logger } from '../../core/Logger.js';
import { SkyRenderer } from './SkyRenderer.js';
import { SkyEnvironmentBaker } from './SkyEnvironmentBaker.js';
import {
  createAtmosphereConfig, atmosphereConfigKey, type AtmosphereConfig,
} from './AtmosphereConfig.js';
import { defineXSky, registerSkyHtml } from './html/index.js';

export interface SkyModuleConfig {
  /** Custom atmosphere parameters. Defaults to Earth-at-sea-level. */
  atmosphere?: Partial<AtmosphereConfig>;
  /**
   * Cube resolution for the environment bake. 64/128/256. Default 128.
   * Larger values cost more per bake but give sharper reflections.
   */
  envResolution?: number;
  /**
   * Angular threshold (radians) — the sun must move by more than this
   * before the environment re-bakes. Default 0.02 (~1.15°).
   */
  envBakeSunThreshold?: number;
  /**
   * Force the environment to re-bake at least this often (seconds). Useful
   * when other scene changes should influence IBL. Default 5 seconds.
   */
  envBakeMaxInterval?: number;
  /**
   * Optional provider for the sun direction (world-space unit vec FROM
   * origin TO sun). Called every frame. If null, sun stays at the last
   * value set via `setSunDirection()`.
   */
  sunDirectionProvider?: () => { x: number; y: number; z: number };
  /** Initial sun illuminance multiplier. Default 20. */
  sunIlluminance?: number;
  /**
   * Camera altitude in km (radius from planet center). The default
   * (bottomRadius + 1 m) places the camera just above ground. Set higher
   * for aerial / space scenarios.
   */
  cameraAltitudeKm?: number;
  /**
   * Track the camera's world Y into the sky observer altitude every frame
   * (meters → km). Default true. Set FALSE for ground-scale games with tall
   * terrain: the real sky barely changes over a few hundred meters, but the
   * tracked LUT observer height re-bakes the sky-view LUT in discrete
   * altitude steps — the sky visibly "pops" as you climb a hill and each
   * re-bake costs CPU mid-traversal. When false the altitude stays at
   * `cameraAltitudeKm` (or the just-above-ground default) forever.
   */
  trackCameraAltitude?: boolean;
  /**
   * Sky evaluation pipeline:
   *   'inline' — v1 per-fragment ray-march (DEFAULT — zero-risk drop-in).
   *   'lut'    — v2 Hillaire 4-LUT path (samples a baked sky-view LUT;
   *              ~10× cheaper per fragment; CPU-bakes on sun/atmosphere
   *              change). Faithful to Hillaire 2020.
   *   'auto'   — 'lut' when the renderer is WebGPU, else 'inline'.
   * Default 'inline'.
   */
  pipeline?: 'inline' | 'lut' | 'auto';
  /**
   * Bake the aerial-perspective froxel volume (v2 LUT path only). The volume
   * + `apSample()` helper are produced, but compositing into the post chain
   * is DEFERRED (see render-sky.md). Default false.
   */
  enableAerialPerspective?: boolean;
}

export class SkyModule implements IModule {
  readonly name = 'Sky';

  private _config: SkyModuleConfig;
  private _atmosphere: AtmosphereConfig;
  private _atmosphereKey: string;
  private _renderer: SkyRenderer | null = null;
  private _baker: SkyEnvironmentBaker | null = null;
  private _system: SkyRuntimeSystem | null = null;

  constructor(config: SkyModuleConfig = {}) {
    this._config = config;
    this._atmosphere = createAtmosphereConfig(config.atmosphere);
    this._atmosphereKey = atmosphereConfigKey(this._atmosphere);
  }

  get renderer(): SkyRenderer {
    if (!this._renderer) throw new Error('SkyModule: not initialized');
    return this._renderer;
  }

  get baker(): SkyEnvironmentBaker {
    if (!this._baker) throw new Error('SkyModule: not initialized');
    return this._baker;
  }

  get atmosphere(): AtmosphereConfig { return this._atmosphere; }

  /**
   * The baked aerial-perspective froxel texture, or null when the LUT
   * pipeline is off / `enableAerialPerspective` is false / not yet baked.
   * Feed it to PostProcessingPipeline's `aerial` config (identity-stable
   * across re-bakes — see SkyLutSet, so the post chain never rebuilds).
   */
  get aerialTexture(): THREE.Data3DTexture | null {
    return this._renderer?.lutSet?.aerial?.texture ?? null;
  }

  /** Set new atmosphere parameters. Forces an environment re-bake next frame. */
  setAtmosphere(cfg: AtmosphereConfig): void {
    this._atmosphere = cfg;
    this._atmosphereKey = atmosphereConfigKey(cfg);
    this._renderer?.setAtmosphere(cfg);
    this._system?.forceBakeNextFrame();
  }

  /** Update the sun direction (world space, FROM origin TO sun). */
  setSunDirection(dir: { x: number; y: number; z: number }): void {
    this._renderer?.setSunDirection(dir);
    this._system?.onSunUpdated(dir);
  }

  /** Update the sun illuminance multiplier. */
  setSunIlluminance(v: number): void {
    this._renderer?.setSunIlluminance(v);
  }

  /** Camera altitude in km from planet center. */
  setCameraAltitudeKm(r: number): void {
    this._renderer?.setCameraAltitudeKm(r);
  }

  init(app: App): void {
    // Resolve the pipeline. 'auto' picks 'lut' on WebGPU (where the LUT
    // sky-view fetch is cheapest) and 'inline' otherwise. The LUT bake is
    // pure CPU, so 'lut' works on WebGL2 too — 'auto' is just a sensible
    // default that keeps the WebGL2 fallback on the simpler v1 path.
    const requested = this._config.pipeline ?? 'inline';
    const resolvedPipeline: 'inline' | 'lut' =
      requested === 'auto'
        ? (app.renderer.isWebGPU ? 'lut' : 'inline')
        : requested;

    // The LUT path uploads float DataTextures with LinearFilter. On WebGL2,
    // linear filtering of float textures needs the OES_texture_float_linear
    // extension, which a few (mostly mobile) GPUs lack — there sky-view sampling
    // silently falls back to nearest filtering (banding). We do NOT change the
    // texture formats (precision tests pin them); we just warn once so the
    // requirement is visible. (No-op on WebGPU, where float-linear is core.)
    if (resolvedPipeline === 'lut' && !app.renderer.isWebGPU) {
      Logger.warn('Sky',
        'LUT pipeline on WebGL2: float-LUT linear filtering requires the ' +
        'OES_texture_float_linear extension. Without it, sky-view/aerial LUT ' +
        'sampling falls back to nearest (banding). See render-sky.md Pitfalls.');
    }

    this._renderer = new SkyRenderer({
      atmosphere: this._atmosphere,
      sunIlluminance: this._config.sunIlluminance,
      pipeline: resolvedPipeline,
      enableAerialPerspective: this._config.enableAerialPerspective,
    });
    this._renderer.setCameraAltitudeKm(
      this._config.cameraAltitudeKm ?? (this._atmosphere.bottomRadius + 0.001),
    );

    // Assign to scene.backgroundNode so Three.js renders the sky as the
    // infinity background of the scene.
    const sceneAny = app.world.scene as unknown as { backgroundNode?: unknown };
    sceneAny.backgroundNode = this._renderer.buildBackgroundNode();
    // Clearing scene.background ensures the NodeMaterial path wins.
    (app.world.scene as THREE.Scene).background = null;

    this._baker = new SkyEnvironmentBaker({
      renderer: app.renderer.native,
      skyRenderer: this._renderer,
      resolution: this._config.envResolution,
    });

    // Register runtime system that handles sun updates + env bake triggers.
    this._system = new SkyRuntimeSystem({
      app,
      module: this,
      baker: this._baker,
      sunProvider: this._config.sunDirectionProvider ?? null,
      bakeSunThreshold: this._config.envBakeSunThreshold ?? 0.02,
      bakeMaxIntervalSeconds: this._config.envBakeMaxInterval ?? 5,
      trackCameraAltitude: this._config.trackCameraAltitude ?? true,
      getAtmosphereKey: () => this._atmosphereKey,
    });
    app.world.addSystem(this._system);

    defineXSky();
    const htmlModule = app.getModule('HtmlEcs') as
      | (IModule & { htmlRegistry?: unknown })
      | undefined;
    if (htmlModule && htmlModule.htmlRegistry) {
      registerSkyHtml(htmlModule.htmlRegistry as never, this);
      Logger.info('Sky', 'HTML integration wired (<x-sky> + sky.* comp keys)');
    }

    Logger.info('Sky', 'Module initialized (physically-based Rayleigh+Mie+ozone)');
  }

  dispose(): void {
    this._baker?.dispose();
    this._renderer?.dispose();
    this._baker = null;
    this._renderer = null;
    this._system = null;
    Logger.info('Sky', 'Module disposed');
  }
}

/**
 * Internal per-frame system that:
 *   - pulls sun direction from the provider (if any), updates SkyRenderer
 *   - triggers SkyEnvironmentBaker.bake() when sun moved enough or the
 *     atmosphere key changed
 *   - assigns the baked PMREM texture to scene.environment
 */
class SkyRuntimeSystem implements ISystem {
  readonly name = 'SkyRuntime';
  readonly phase = LoopPhase.RenderSub;
  readonly subPhase = SubPhase.Update;
  readonly priority = 6;

  private readonly _app: App;
  private readonly _module: SkyModule;
  private readonly _baker: SkyEnvironmentBaker;
  private readonly _sunProvider: (() => { x: number; y: number; z: number }) | null;
  private readonly _threshold: number;
  private readonly _maxInterval: number;
  private readonly _getAtmosphereKey: () => string;
  private readonly _trackAltitude: boolean;

  private _lastSunDir = { x: 0, y: 1, z: 0 };
  private _lastBakeTime = -Infinity;
  private _lastAtmKey = '';
  private _hasBaked = false;
  private _forceNextBake = true;
  private _elapsedSeconds = 0;
  /** Amortized bake cycle in flight (one baker step per frame). */
  private _baking = false;
  /** Sun below astronomical twilight last frame (dark-sky bake damping). */
  private _wasDark = false;

  constructor(args: {
    app: App;
    module: SkyModule;
    baker: SkyEnvironmentBaker;
    sunProvider: (() => { x: number; y: number; z: number }) | null;
    bakeSunThreshold: number;
    bakeMaxIntervalSeconds: number;
    getAtmosphereKey: () => string;
    trackCameraAltitude: boolean;
  }) {
    this._app = args.app;
    this._module = args.module;
    this._baker = args.baker;
    this._sunProvider = args.sunProvider;
    this._threshold = args.bakeSunThreshold;
    this._maxInterval = args.bakeMaxIntervalSeconds;
    this._getAtmosphereKey = args.getAtmosphereKey;
    this._trackAltitude = args.trackCameraAltitude;
  }

  forceBakeNextFrame(): void {
    this._forceNextBake = true;
  }

  onSunUpdated(dir: { x: number; y: number; z: number }): void {
    // Mutate in place — this object is compared, never aliased, and the
    // per-frame literal was steady GC churn on the always-on sky path.
    this._lastSunDir.x = dir.x;
    this._lastSunDir.y = dir.y;
    this._lastSunDir.z = dir.z;
  }

  execute(dt: number, _world: World): void {
    this._elapsedSeconds += dt;

    if (this._sunProvider) {
      const dir = this._sunProvider();
      const prev = this._lastSunDir;
      const moved = 1 - (prev.x * dir.x + prev.y * dir.y + prev.z * dir.z);
      this._module.setSunDirection(dir);
      prev.x = dir.x; prev.y = dir.y; prev.z = dir.z;
      if (moved > this._threshold * this._threshold * 0.5 && dir.y >= DARK_SKY_SUN_Y) {
        // The dot-based drift uses a "cosine-derived" check; threshold² · 0.5
        // is the small-angle approximation of (1 - cos θ). Past astronomical
        // twilight (dark-sky damping below) drift changes nothing visible in
        // the env — the stretched interval carries the cycle instead.
        this._forceNextBake = true;
      }
    }
    // Dark-sky damping: below astronomical twilight the sky env is static, so
    // the (PMREM-dominated) re-bake buys nothing — live-profiled 2026-07-31 as
    // the last recurring hitch during night play. One forced bake fires on
    // each dark-boundary crossing so dusk/dawn stay correct.
    const dark = this._lastSunDir.y < DARK_SKY_SUN_Y;
    if (dark !== this._wasDark) {
      this._wasDark = dark;
      this._forceNextBake = true;
    }

    // Camera altitude tracking — read from first camera if present. Skipped
    // when trackCameraAltitude:false (ground-scale games with tall terrain:
    // the sky barely changes over hundreds of meters, but tracking re-bakes
    // the sky-view LUT in visible altitude steps as you climb).
    const cam = (this._app.renderer.cameras as unknown as {
      main?: THREE.Camera; getEntries?: () => Array<{ camera: THREE.Camera }>;
    });
    const camObj = this._trackAltitude ? (cam.main ?? cam.getEntries?.()[0]?.camera) : undefined;
    if (camObj) {
      const pos = camObj.getWorldPosition(_tmpPos);
      // Convert world meters above ground → radius from planet center in km.
      // We assume camera Y is altitude above ground (flat-earth local scene).
      // For a round-earth scene you'd use length(pos - planetCenter) * 0.001.
      const altKm = Math.max(0, pos.y * 0.001);
      this._module.setCameraAltitudeKm(
        this._module.atmosphere.bottomRadius + altKm + 0.001,
      );
    }

    // Atmosphere-change detection
    const currentKey = this._getAtmosphereKey();
    if (currentKey !== this._lastAtmKey) {
      this._lastAtmKey = currentKey;
      this._forceNextBake = true;
    }

    // v2 LUT path: regenerate dirty LUTs (sky-view re-bakes on sun/altitude
    // drift; transmittance/multi-scatter on atmosphere change). A re-bake
    // means the sky changed, so force a PMREM env re-bake to keep IBL in
    // sync. No-op in inline mode (returns null).
    const lutResult = this._module.renderer.updateLuts(this._forceNextBake);
    if (lutResult && (lutResult.tierA || lutResult.tierB)) {
      this._forceNextBake = true;
    }

    // Bake driver — AMORTIZED (live-profiled 2026-07-31): the one-shot bake
    // was a recurring ~120 ms tick (6 cube faces + the PMREM chain in one
    // frame). One baker step per frame spreads the faces; only the PMREM
    // frame remains a (smaller) single-frame cost. Drift/atmosphere changes
    // during a cycle re-set _forceNextBake and queue the next cycle.
    if (this._baking) {
      const r = this._baker.bakeStep();
      if (r === 'done') {
        const env = this._baker.output;
        if (env) {
          (this._app.world.scene as THREE.Scene).environment = env;
        }
        this._hasBaked = true;
        this._lastBakeTime = this._elapsedSeconds;
        this._baking = false;
      } else if (r === 'failed') {
        this._forceNextBake = true; // retry a fresh cycle next frame
        this._baking = false;
      }
      return;
    }
    const effInterval = dark ? this._maxInterval * 8 : this._maxInterval;
    const intervalElapsed = (this._elapsedSeconds - this._lastBakeTime) > effInterval;
    const shouldBake = this._forceNextBake
                    || (this._hasBaked && intervalElapsed)
                    || !this._hasBaked;
    if (shouldBake) {
      this._forceNextBake = false;
      this._baking = true;
      // First face this frame — the LUT updates above already spent this
      // frame's sky budget, but a single 128² face render is small.
      if (this._baker.bakeStep() === 'failed') {
        this._forceNextBake = true;
        this._baking = false;
      }
    }
  }

  dispose(): void {
    // SkyModule.dispose handles baker cleanup.
  }
}

const _tmpPos = new THREE.Vector3();

/** Sun-direction Y below which the sky counts as astronomically dark (≈ sun
 *  20° under the horizon — past the last twilight glow). */
const DARK_SKY_SUN_Y = -0.35;
